import React, { useState } from 'react';
import { Card, pnlCls, money } from './LearningParts';

const REASON_TR = { stop: 'stop', tp: 'hedef / kâr', time: 'zaman', manual: 'elle', import: 'içe aktarılan', other: 'diğer' };
const KIND_TR = { AC: '🎯 AÇ', EMIR_KOY: '🔭 EMİR KOY', KUCUK_AC: '🚀 KÜÇÜK AÇ', ETIKET: '🔥 ETİKET', PATLAMA: '💥 PATLAMA', ISINMA: '🔥 ISINIYOR', HABER: '📰 HABER', OYNAK: '🌊 OYNAK', ARALIK: '📐 ARALIK' };
const fmtDay = (k) => (k.length === 7 ? `${k.slice(5)}.${k.slice(0, 4)}` : `${k.slice(8)}.${k.slice(5, 7)}`);
const fmtTs = (ts) => new Date(ts * 1000).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

function Tile({ label, value, hint, cls = 'text-slate-100', testId }) {
    return (
        <div className="rounded-md border border-slate-800 bg-slate-950/50 px-2.5 py-2 min-w-[120px]" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className={`font-mono font-bold text-sm ${cls}`}>{value}</div>
            {hint && <div className="text-[10px] text-slate-500">{hint}</div>}
        </div>
    );
}

/** Dönem özeti: net, brüt/komisyon, isabet, ort. kâr/zarar, kâr faktörü, en iyi/kötü, kapanış nedenleri, karar türleri. */
export function EarningsSummary({ s }) {
    const rr = s.avg_loss ? Math.abs(s.avg_win / s.avg_loss) : null;
    return (
        <div className="space-y-2" data-testid="earnings-summary">
            <div className="flex flex-wrap gap-2">
                <Tile label="Net kazanç" value={money(s.net)} cls={pnlCls(s.net)} hint={s.fees > 0 ? `brüt ${money(s.gross)} · komisyon ${Number(s.fees).toFixed(2)}$` : 'komisyon düşülmüş net'} testId="earnings-net" />
                <Tile label="İşlem" value={`${s.trades} · ${s.wins} kâr / ${s.losses} zarar`} hint={s.win_rate != null ? `isabet %${s.win_rate}` : '—'} testId="earnings-trades" />
                <Tile label="Ort. kâr / ort. zarar" value={s.trades ? `${Number(s.avg_win).toFixed(2)}$ / ${Number(s.avg_loss).toFixed(2)}$` : '—'} cls={rr != null ? (rr >= 1 ? 'text-emerald-300' : 'text-amber-300') : 'text-slate-100'}
                      hint={rr != null ? `oran ${rr.toFixed(2)} · başabaş için %${Math.round(100 / (1 + rr))} isabet` : ''} testId="earnings-rr" />
                <Tile label="Kâr faktörü" value={s.trades ? s.profit_factor : '—'} cls={s.profit_factor >= 1 ? 'text-emerald-300' : 'text-rose-300'} hint="brüt kâr ÷ brüt zarar" testId="earnings-pf" />
                <Tile label="İşlem başına" value={s.trades ? money(s.expectancy) : '—'} cls={pnlCls(s.expectancy)} testId="earnings-exp" />
                {s.best && <Tile label="En iyi" value={`${s.best.symbol.replace('USDT', '')} ${money(s.best.pnl)}`} cls="text-emerald-300" hint={fmtTs(s.best.closed_at)} testId="earnings-best" />}
                {s.worst && <Tile label="En kötü" value={`${s.worst.symbol.replace('USDT', '')} ${money(s.worst.pnl)}`} cls="text-rose-300" hint={fmtTs(s.worst.closed_at)} testId="earnings-worst" />}
            </div>
            {s.trades > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
                    <span data-testid="earnings-reasons">Kapanış: {Object.entries(s.by_reason).map(([k, v]) => `${REASON_TR[k] || k} ${v.n} (${money(v.net)})`).join(' · ')}</span>
                    <span data-testid="earnings-kinds">Tür: {Object.entries(s.by_kind).sort((a, b) => b[1].n - a[1].n).map(([k, v]) => `${KIND_TR[k] || k} ${v.n} (${money(v.net)})`).join(' · ')}</span>
                </div>
            )}
        </div>
    );
}

/** Günlük net çubukları (yeşil +, kırmızı −). */
export function DailyBars({ series }) {
    if (!series?.length) return null;
    const max = Math.max(...series.map((d) => Math.abs(d.net)), 1);
    const shown = series.slice(-62);
    return (
        <Card title="Günlük net" testId="earnings-bars">
            <div className="flex items-end gap-[3px] h-28 border-b border-slate-700" data-testid="earnings-bars-chart">
                {shown.map((d) => (
                    <div key={d.key} className="flex-1 flex flex-col justify-end items-center h-full" title={`${fmtDay(d.key)}: ${money(d.net)} · ${d.trades} işlem`}>
                        <div className="w-full flex flex-col justify-end" style={{ height: '50%' }}>
                            {d.net > 0 && <div className="w-full bg-emerald-400/80 rounded-t" style={{ height: `${(d.net / max) * 100}%` }} />}
                        </div>
                        <div className="w-full flex flex-col justify-start" style={{ height: '50%' }}>
                            {d.net < 0 && <div className="w-full bg-rose-400/80 rounded-b" style={{ height: `${(-d.net / max) * 100}%` }} />}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex justify-between text-[10px] text-slate-500"><span>{fmtDay(shown[0].key)}</span><span>{fmtDay(shown[shown.length - 1].key)}</span></div>
        </Card>
    );
}

/** Günlük / aylık tablo. */
export function SeriesTable({ title, rows, testId }) {
    const list = [...(rows || [])].reverse();
    return (
        <Card title={title} testId={testId}>
            {list.length === 0 && <div className="text-[11px] text-slate-500">Bu dönemde kapanan işlem yok.</div>}
            {list.length > 0 && (
                <table className="w-full text-[11px]">
                    <thead><tr className="text-left text-slate-500"><th className="py-0.5">{title === 'Aylık' ? 'Ay' : 'Gün'}</th><th>İşlem</th><th>Kâr / zarar</th><th>İsabet</th><th className="text-right">Komisyon</th><th className="text-right">Net</th></tr></thead>
                    <tbody>
                        {list.slice(0, 31).map((d) => (
                            <tr key={d.key} className="border-t border-slate-800" data-testid={`${testId}-row-${d.key}`}>
                                <td className="py-0.5 font-mono text-slate-300">{fmtDay(d.key)}</td>
                                <td className="font-mono">{d.trades}</td>
                                <td className="font-mono"><span className="text-emerald-300">{d.wins}</span> / <span className="text-rose-300">{d.losses}</span></td>
                                <td className="font-mono text-slate-400">{d.win_rate != null ? `%${d.win_rate}` : '—'}</td>
                                <td className="font-mono text-right text-slate-500">{Number(d.fees).toFixed(2)}$</td>
                                <td className={`font-mono text-right font-bold ${pnlCls(d.net)}`}>{money(d.net)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Card>
    );
}

const SC_REASON = { tp: '✅ kâr alındı', stop: '❌ stop', time: '⏱ zaman', manual: '✋ elle', protect_fail: '⚠️ koruma', stopped: '🛑 bot durdu' };

export function resultLabel(c) {
    const pnl = Number(c.pnl_usd || 0);
    if (c.close_reason === 'tp' && pnl < 0) return '⚠ hedef · zararla kapandı';
    return SC_REASON[c.close_reason] || c.close_reason;
}

/** Dönemdeki işlemler (son 200). */
export function TradesTable({ rows, bot, onSelect }) {
    const [open, setOpen] = useState(false);
    if (!rows?.length) return null;
    return (
        <Card title={`İşlemler · ${rows.length}${rows.length === 200 ? '+' : ''}`} testId="earnings-trades-table"
              right={<button type="button" onClick={() => setOpen((v) => !v)} className="text-[11px] text-emerald-200 hover:text-white" data-testid="earnings-trades-toggle">{open ? 'gizle' : 'göster'}</button>}>
            {open && (
                <div className="max-h-96 overflow-auto">
                    <table className="w-full text-[11px]">
                        <thead><tr className="text-left text-slate-500"><th className="py-0.5">Zaman</th><th>Coin</th><th>Tür</th><th>Yön</th><th>Giriş → Çıkış</th><th>Sonuç</th><th className="text-right">Net</th></tr></thead>
                        <tbody>
                            {rows.map((c, i) => (
                                <tr key={`${c.symbol}-${c.closed_at}-${i}`} className="border-t border-slate-800 hover:bg-slate-800/50 cursor-pointer" onClick={() => onSelect && onSelect(c.symbol)} data-testid={`earnings-trade-${i}`}>
                                    <td className="py-0.5 font-mono text-slate-400">{fmtTs(c.closed_at)}</td>
                                    <td className="font-mono font-bold text-slate-100">{c.symbol.replace('USDT', '')}</td>
                                    <td className="text-slate-400">{KIND_TR[c.kind] || c.kind || '—'}</td>
                                    <td className={c.side === 'LONG' ? 'text-emerald-300' : 'text-rose-300'}>{c.side} <span className="text-slate-500">{c.leverage}x</span></td>
                                    <td className="font-mono text-slate-300">{c.entry != null ? `${c.entry} → ${c.exit ?? '—'}` : '—'}</td>
                                    <td className={Number(c.pnl_usd) < 0 && c.close_reason === 'tp' ? 'text-amber-300' : 'text-slate-300'}>{bot === 'scalper' ? resultLabel(c) : (REASON_TR[c.close_reason] || c.close_reason)}</td>
                                    <td className={`font-mono text-right font-bold ${pnlCls(c.pnl_usd)}`}>{money(c.pnl_usd)} <span className="font-normal text-[10px] text-slate-500">({c.pnl_pct >= 0 ? '+' : ''}{c.pnl_pct}%)</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}
